import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import DashboardLayout from "../../components/DashboardLayout";
import WeaveLoader from "../../components/common/WeaveLoader";
import { projectsService } from "../../services/projects";
import { setCurrentProject, setProjects } from "../../redux/project/projectSlice";
import { sitePath } from "../../utils/urls";

const ProjectSettings = ({ currentUser }) => {
  const dispatch = useDispatch();
  const { currentProject, loaded: projectsLoaded } = useSelector(
    (state) => state.project
  );
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const userId = currentUser?.identity?.id;

  const isOwner = currentProject?.memberships?.some(
    (membership) => membership.user_id === userId && membership.role === "owner"
  );
  const isArchived = currentProject?.is_archived || currentProject?.status === "archived";

  useEffect(() => {
    setName(currentProject?.name || "");
    setSaveError(null);
    setSaveSuccess(false);
  }, [currentProject?.hash, currentProject?.name]);

  const refreshProjects = () => {
    projectsService.listProjects({ page_size: 500 }, (response) => {
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results ?? [];
      dispatch(setProjects(results));
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!currentProject?.hash || !name.trim()) {
      setSaveError("Project name is required");
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    projectsService.updateProject(
      currentProject.hash,
      { name: name.trim() },
      (response) => {
        const updated = response.data;
        dispatch(setCurrentProject(updated));
        refreshProjects();
        setSaving(false);
        setSaveSuccess(true);
      },
      (error) => {
        setSaveError(
          error?.response?.data?.error
            || error?.response?.data?.name
            || "Could not update project"
        );
        setSaving(false);
      }
    );
  };

  return (
    <DashboardLayout title="Project settings" currentUser={currentUser}>
      <div className="max-w-xl space-y-8">
        {!projectsLoaded ? (
          <div className="flex justify-center py-16">
            <WeaveLoader size={64} />
          </div>
        ) : !currentProject ? (
          <div className="bg-theme-main rounded-lg border border-theme-secondary p-6 text-theme-text">
            <p className="opacity-80 mb-4">
              Select a project from the sidebar, or create one to manage settings.
            </p>
            <Link
              to="/"
              className="text-theme-primary hover:opacity-80 underline"
            >
              Go to sites
            </Link>
          </div>
        ) : (
          <>
            <div>
              <h2 className="text-2xl font-semibold text-theme-text mb-4">
                General
              </h2>
              {!isOwner && (
                <p className="mb-4 text-sm text-theme-text opacity-60">
                  Only project owners can edit this project.
                </p>
              )}
              {isArchived && (
                <p className="mb-4 text-sm text-theme-text opacity-60">
                  This site is archived. Unarchive or delete it from{" "}
                  <Link to={sitePath(currentProject, "settings/management/")} className="underline text-theme-primary">
                    Site settings
                  </Link>
                  .
                </p>
              )}
              {saveError && (
                <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded text-sm text-theme-text">
                  {saveError}
                </div>
              )}
              {saveSuccess && (
                <div className="mb-4 p-3 bg-green-500 bg-opacity-20 border border-green-500 rounded text-sm text-theme-text">
                  Project updated.
                </div>
              )}
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label
                    htmlFor="project_name"
                    className="block text-sm font-medium text-theme-text mb-2"
                  >
                    Project name
                  </label>
                  <input
                    id="project_name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isOwner || saving || isArchived}
                    required
                    className="appearance-none block w-full px-3 py-2 border border-theme-secondary rounded-md shadow-sm text-theme-text placeholder-opacity-60 focus:outline-none focus:ring-theme-primary focus:border-theme-primary sm:text-sm disabled:opacity-60"
                  />
                </div>
                {isOwner && !isArchived && (
                  <button
                    type="submit"
                    disabled={saving || name.trim() === currentProject.name}
                    className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 disabled:opacity-50"
                    style={{ backgroundColor: "var(--theme-primary)" }}
                  >
                    {saving ? "Saving..." : "Save changes"}
                  </button>
                )}
              </form>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProjectSettings;
